import { z } from 'zod'

/**
 * Contrato de eventos do woctools-socketio v1 (fonte: `woctools-socketio/contracts/events.v1.json`
 * + `woc-rebuild/socketio-contracts.md`, Agent F). Só os eventos consumidos pelo FE v2 estão aqui.
 *
 * Semântica: todo evento é um SINAL DE INVALIDAÇÃO. O payload não traz dados de negócio, então o
 * FE refaz a consulta HTTP correspondente. Os nomes legados (`message`, `woctools-frontend-*`)
 * não são escutados.
 */
export const CONTRACT_VERSION = 1

export const REALTIME_EVENTS = {
  scheduleSubmitUpdated: 'schedule.submit.updated',
  scheduleCompanyUpdated: 'schedule.company.updated',
  scheduleSellerUpdated: 'schedule.seller.updated',
  scheduleRequestUpdated: 'schedule.request.updated',
  govendasAccountsChanged: 'govendas.accounts.changed',
  govendasUsersChanged: 'govendas.users.changed',
} as const

export type RealtimeEventKey = keyof typeof REALTIME_EVENTS
export type RealtimeEventName = (typeof REALTIME_EVENTS)[RealtimeEventKey]

export const SCHEDULE_EVENTS: RealtimeEventKey[] = [
  'scheduleSubmitUpdated',
  'scheduleCompanyUpdated',
  'scheduleSellerUpdated',
  'scheduleRequestUpdated',
]

const id = z.string().regex(/^[A-Za-z0-9_.:-]{1,128}$/)

/** Envelope v1 (`payload_schema` do contrato). Campos de id são opcionais por evento. */
export const realtimeEnvelopeSchema = z.object({
  v: z.literal(1),
  event: z.string(),
  event_id: z.string(),
  occurred_at: z.string(),
  source: z.enum(['service', 'legacy-anonymous', 'kafka-bridge', 'pulse']),
  publisher: z.string().max(128).optional(),
  request_id: id.optional(),
  schedule_id: id.optional(),
  company_id: id.optional(),
  seller_id: id.optional(),
  coalesced: z.number().int().min(1),
})
export type RealtimeEnvelope = z.infer<typeof realtimeEnvelopeSchema>

/** O que o handler recebe: o envelope validado, ou `null` se veio fora do contrato. */
export type RealtimePayload = RealtimeEnvelope | null

export const TOPICS = ['schedules', 'govendas', 'integration', 'infra'] as const
export type RealtimeTopic = (typeof TOPICS)[number]

/** Comandos cliente → servidor (sempre com ack). */
export interface SubscribeCommand {
  topic: RealtimeTopic
  schedule_id?: string
}

export const commandAckSchema = z.union([
  z.object({ ok: z.literal(true), room: z.string().optional() }).passthrough(),
  z.object({ ok: z.literal(false), error: z.object({ code: z.string() }) }).passthrough(),
])
export type CommandAck = z.infer<typeof commandAckSchema>

/** `err.data` do `connect_error` (recusa no handshake). */
export const connectErrorDataSchema = z.object({
  code: z.enum(['unauthorized', 'forbidden']).or(z.string()),
  reason: z.string().optional(),
})
